import { Search } from 'lucide-react'
import { useState } from 'react'
import { Input } from '@/components/ui/input'
import { trpc } from '@/lib/trpc'

interface UserSearchPickerProps {
  selectedUserId: string | null
  onSelect: (userId: string | null) => void
}

/**
 * Search-and-select platform-user picker (LG-982). Still not shared with
 * `PlatformUserLink`, which has a near-identical search UI: the two differ in
 * what selection means (this one picks a user for a client that does not
 * exist yet; that one re-points an existing client's link) and in their empty
 * and selected states. Folding them together is tracked on LG-1006 — the
 * original reason for the split, that PlatformUserLink's mutations were mid
 * rework by the CoachingClient.userId NOT NULL migration, has expired.
 */
export function UserSearchPicker({
  selectedUserId,
  onSelect,
}: UserSearchPickerProps) {
  const [searchQuery, setSearchQuery] = useState('')

  const usersQuery = trpc.admin.clients.searchUsers.useQuery(
    { query: searchQuery },
    { enabled: searchQuery.length >= 2 },
  )

  const selected = usersQuery.data?.find((u) => u.id === selectedUserId)

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search
          size={14}
          className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2"
        />
        <Input
          type="text"
          aria-label="Search platform users"
          value={
            selected
              ? `${selected.firstName} ${selected.lastName}`
              : searchQuery
          }
          onChange={(e) => {
            setSearchQuery(e.target.value)
            onSelect(null)
          }}
          placeholder="Search users by name or email..."
          className="pl-8"
        />
      </div>
      {!selectedUserId && usersQuery.data && usersQuery.data.length > 0 && (
        <ul className="border-border max-h-40 overflow-y-auto rounded-md border">
          {usersQuery.data.map((u) => (
            <li key={u.id}>
              <button
                type="button"
                onClick={() => onSelect(u.id)}
                className="hover:bg-muted w-full px-3 py-2 text-left text-sm"
              >
                <span className="text-foreground font-medium">
                  {u.firstName} {u.lastName}
                </span>
                <span className="text-muted-foreground ml-2 text-xs">
                  {u.email}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {!selectedUserId &&
        usersQuery.data &&
        usersQuery.data.length === 0 &&
        searchQuery.length >= 2 && (
          <p className="text-muted-foreground text-xs">
            No users found. A brand-new platform user may take a moment to sync
            — try the Promote option on the Users tab instead.
          </p>
        )}
    </div>
  )
}
